import React, { useState, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { agencySettingsAPI } from '../../lib/api';
import { ConfidentialitySignGate } from '../ConfidentialitySignGate';
import CallManager from '../CallManager';
import { SubscriptionExpiryWarning } from '../SubscriptionExpiryWarning';
import { ActionCountersProvider, useActionCounters } from '../../context/ActionCountersContext';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MaliClock } from '../MaliClock';
import { NotificationBell } from '../NotificationBell';
import { OfflineBanner } from '../OfflineBanner';
import FloatingChat from '../FloatingChat';
import { AccessProvider, ModuleGuard } from '../../context/AccessContext';
import { StaffUrgentAlert } from '../DashboardNewsTicker';

const HeaderActionBadge = () => {
  const { total } = useActionCounters();
  if (!total) return null;
  return (
    <Link
      to="/"
      className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 transition-colors"
      title={`${total} action(s) à effectuer`}
      data-testid="header-action-counter"
    >
      <Bell className="w-3.5 h-3.5" />
      <span className="text-[11px] font-bold tabular-nums">{total > 99 ? '99+' : total}</span>
    </Link>
  );
};

export const MainLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await agencySettingsAPI.get();
        const url = response.data?.logo_url;
        if (url) {
          setLogoUrl(url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);
        }
      } catch (e) {
        /* silently ignore */
      }
    };
    fetchLogo();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AccessProvider>
    <ActionCountersProvider>
      <div className="min-h-screen bg-app-ambient ae-wax-bg-light">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="main-content relative">
        {/* Subscription expiry warning banner (preventif - 7j avant) */}
        <SubscriptionExpiryWarning />

        {/* Logo watermark overlay */}
        {logoUrl && (
          <div
            className="pointer-events-none fixed inset-0 z-[50] flex items-center justify-center lg:pl-64"
            data-testid="logo-watermark"
          >
            <img
              src={logoUrl}
              alt=""
              className="w-[350px] h-[350px] object-contain select-none"
              style={{ opacity: 0.07, filter: 'grayscale(20%)' }}
              draggable={false}
            />
          </div>
        )}

        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-[#B85C38]/15 px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-[#F5EFE1] rounded-lg transition-colors"
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-5 h-5 text-[#2B3A2C]" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-md"
                   style={{ background: 'linear-gradient(135deg, #B85C38 0%, #D07A55 100%)' }}>
                <span className="text-white font-bold text-sm font-display">GH</span>
              </div>
              <span className="font-bold text-[#2B3A2C] text-sm tracking-tight font-display">GAB&apos;S HABITAT</span>
            </div>
            <HeaderActionBadge />
            <NotificationBell />
          </div>
          <div className="mt-1.5 flex justify-end">
            <MaliClock compact />
          </div>
        </header>

        {/* Desktop floating action counter + clock (top-right) */}
        <div className="hidden lg:flex fixed top-4 right-6 z-30 items-center gap-2">
          <MaliClock />
          <HeaderActionBadge />
          <NotificationBell />
        </div>

        {/* Main content */}
        <main className="relative z-10 p-4 md:p-6 lg:p-8 lg:pt-20">
          <ModuleGuard><Outlet /></ModuleGuard>
          <StaffUrgentAlert />
        </main>
      </div>
      
      <OfflineBanner />
      <ConfidentialitySignGate />
      <CallManager />
      <FloatingChat />
      </div>
    </ActionCountersProvider>
    </AccessProvider>
  );
};
